import { FC, useCallback, useEffect, useState } from 'react'
import { AlertModal } from '@tuhi/components'
import { useDispatch } from 'react-redux'
import { closeModal } from 'app/store/slices/modalSlice'
import { useSignOut } from 'app/api/auth/repository/authenticationRepository'

interface Props {
    signOutInMinutes: number
    onSignOut: () => void
    onResume: () => void
}

export const AreYouThereModal: FC<Props> = ({
    signOutInMinutes,
    onSignOut,
    onResume,
}) => {
    const dispatch = useDispatch()
    const signOut = useSignOut()

    const [timeLeft, setTimeLeft] = useState(signOutInMinutes * 60) // 2 minutes in seconds

    const dismiss = useCallback(() => {
        dispatch(closeModal())
    }, [dispatch])

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${minutes}:${secs.toString().padStart(2, '0')}`
    }

    const handleSignOut = () => {
        dismiss()

        signOut().then(() => {
            onSignOut()
        })
    }

    const handleResumeSession = () => {
        dismiss()
        onResume()
    }

    useEffect(() => {
        if (timeLeft <= 0) {
            handleSignOut()
            return
        }

        const timeout = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- One tick per second; handleSignOut is only called at zero, and re-running on its identity would restart the 1s tick
    }, [timeLeft])

    return (
        <AlertModal
            title="Are you still there?"
            subtitle={`Your session is about to expire, do you want to continue your session? Time left: ${formatTime(timeLeft)}`}
            secondaryButton={{
                title: 'Log out',
                onClick: handleSignOut,
                type: 'negative',
            }}
            primaryButton={{
                title: 'Resume session',
                onClick: handleResumeSession,
            }}
            closeButtonOnClick={handleResumeSession}
        />
    )
}
